import { Event } from '../eventFactory/event';
import { EventBus } from '../eventBus';
import { EventType } from '../eventFactory/eventTypes';
import { EventBusAdapter, EventBusAdapterFactory } from './eventBusAdapter';

export class PostMessageAdapter implements EventBusAdapter {
  private eventBus: EventBus;
  private unsubscribers: Array<() => void> = [];
  private messageListener?: (event: MessageEvent) => void;
  private readonly targetOrigin: string;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
    this.targetOrigin = this.getTargetOrigin();
  }

  static factory(): EventBusAdapterFactory {
    return {
      create: (eventBus: EventBus) => new PostMessageAdapter(eventBus),
    };
  }

  start(): void {
    this.setupOutgoingBridge();
    this.setupIncomingBridge();
  }

  stop(): void {
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];
  }

  private setupOutgoingBridge(): void {
    const unsubscribe = this.eventBus.on(
      '*',
      (event: Event) => this.sendPostMessage(event)
    );

    this.unsubscribers.push(unsubscribe);
  }

  private setupIncomingBridge(): void {
    this.messageListener = (messageEvent: MessageEvent) => {
      try {
        const { data } = messageEvent;

        if (!data || typeof data !== 'object' || !data.task) {
          return;
        }

        const event: Event = {
          type: data.task,
          payload: data.payload,
        };

        this.eventBus.emit(event, { skipWildcardSubscribers: true });
      } catch (error) {
        console.error('[PostMessageAdapter] Error processing incoming message:', error);
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  private sendPostMessage(event: Event): void {
    try {
      const message = this.toPostMessage(event);
      window.parent.postMessage(message, this.targetOrigin);

      if ('webkit' in window) {
        (window as any).webkit.messageHandlers.mobileApp?.postMessage(message);
      }
    } catch (error) {
      console.error('[PostMessageAdapter] Error sending to parent:', error);
    }
  }

  private toPostMessage(event: Event): { task: EventType, payload: unknown } {
    return { task: event.type, payload: event.payload };
  }

  private getTargetOrigin(): string {
    const params = new URLSearchParams(window.location.search);
    const parentHost = params.get('host');

    let targetOrigin = '/';

    if (parentHost && parentHost !== '*') {
      const url = new URL(parentHost);
      targetOrigin = url.origin;
    }

    return targetOrigin;
  }
}
